import { type JSX } from 'react';
import { Navigate, Outlet, generatePath, useLocation, useParams } from 'react-router';

import { PublicHeader } from 'Components/PublicHeader/PublicHeader';
import { TPRM_PUBLIC_LOGIN } from 'Config/PathTemplates';
import { BasicAuthHandler } from 'Helpers/Auth/BasicAuth/BasicAuthHandler';
import { isSessionForService } from 'Helpers/Auth/BasicAuth/Utils';

/** Query parameter used to remember the page a third-party user was trying to reach before being redirected to `VSCLogin`. */
export const POST_LOGIN_REDIRECT_PARAM = 'next';

interface UrlParams {
    client_id: string;
    third_party_id: string;
    service_id: string;
}

export interface RequirePublicDDQAuthProps {
    authHandler: BasicAuthHandler;
}

/**
 * Guards TPRM pages accessed by third-party users. Used as a "layout" route (renders an `<Outlet />`), wrapping all pages for third-party users except for `VSCLogin`.
 * The user is redirected to `VSCLogin` for the client + third party + service in the current URL if they have not logged in (i.e., there are no stored credentials) or if they are logged in for a different service.
 */
export const RequirePublicDDQAuth = (props: RequirePublicDDQAuthProps): JSX.Element => {
    const { client_id, third_party_id, service_id } = useParams<keyof UrlParams>() as UrlParams;
    const location = useLocation();

    const loginPath = generatePath(TPRM_PUBLIC_LOGIN, { client_id, third_party_id, service_id });

    if (!isSessionForService(props.authHandler.getAuthenticatedSession(), client_id, third_party_id, service_id)) {
        const attemptedPath = `${location.pathname}${location.search}`;
        return <Navigate to={`${loginPath}?${POST_LOGIN_REDIRECT_PARAM}=${encodeURIComponent(attemptedPath)}`} replace />;
    }

    return (
        <>
            <PublicHeader authHandler={props.authHandler} logoutDestination={loginPath} />
            <Outlet />
        </>
    );
};
